import React, { useMemo, useRef, useState } from 'react';
import { ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';
import { SOURCES, normalizeMatrix, perTimestampScores } from './dayDetailMath';
import { SOURCE_COLOR, NOT_APPLICABLE_HATCH, withAlpha } from './sourceColors';
import {
  getHourMinuteInResourceTz,
  getResourceTimezoneLabel,
  getResourceTimezoneLabels,
} from '../../../../../utils/datetime';

const formatTime = (iso) => {
  if (!iso) return '';
  return getHourMinuteInResourceTz(iso) || iso;
};

const formatNumber = (v, digits = 3) => {
  if (v == null || !Number.isFinite(v)) return '—';
  return Number(v).toFixed(digits);
};

// Per-timestamp MSE is in MW² and can reach the millions; show a compact form
// in the narrow column (full precision stays in the cell title).
const compactMSE = (v) => {
  if (v == null || !Number.isFinite(v)) return '—';
  if (Math.abs(v) < 5e-4) return '0'; // rounds to zero — no decimals
  if (Math.abs(v) >= 1000) {
    return new Intl.NumberFormat('en', { notation: 'compact', minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(v);
  }
  return Number(v).toFixed(3);
};

const noop = () => {};

/**
 * Renders the per-timestamp × per-slot residual heatmap plus a per-timestamp
 * column on the left showing the MSE (mean squared residual) that derives from
 * each row. Hover a row to preview its build-up strip; click to pin it.
 *
 * Intraday vs D+N is decided by `horizonOffset` (not by the matrix's stored
 * weighting, which is "uniform" for both since the framework moved to an
 * arithmetic mean):
 *  - Intraday (horizonOffset === 0): wide 24-column grid, header shows lead
 *    time per slot column (constant across rows by construction).
 *  - D+N (horizonOffset >= 1): the columns are the submissions covering the
 *    slice; the header shows each submission's gate-closure clock.
 */
export default function SlotMatrixHeatmap({
  matrixJson,
  horizonOffset,
  selectedIndex = null,
  onSelect = noop,
  onPreview = noop,
}) {
  const [hover, setHover] = useState(null); // {t, s} or {row: number}
  const [copyState, setCopyState] = useState('idle'); // 'idle' | 'ok' | 'err'
  const copyResetRef = useRef(null);

  const matrix = useMemo(() => normalizeMatrix(matrixJson), [matrixJson]);
  const perTs = useMemo(() => perTimestampScores(matrixJson), [matrixJson]);

  // D+N drill-downs label columns by gate-closure clock; intraday by lead time.
  const isDN = typeof horizonOffset === 'number' && horizonOffset >= 1;

  const buildTsv = () => {
    if (!matrix) return '';
    const slotIdx = Array.from({ length: matrix.slotCount }, (_, i) => i);
    // Header row: timestamp, MSE, then one column per slot annotated with
    // its lead time (taken from the first row's lead_hours — leads are
    // identical across timestamps for a given slot index by construction).
    const firstLeadRow = matrix.leadHours[0] || [];
    const header = [
      'timestamp',
      'per_timestamp_mse',
      ...slotIdx.map((s) => `slot_${s + 1}_lead_${firstLeadRow[s] ?? '?'}h`),
    ];
    // Data rows. Each slot cell encodes residual + provenance as
    // "<residual>;<source>" so the receiver can reconstruct both. Missing
    // slots are emitted as ";missing".
    const rows = matrix.timestamps.map((iso, t) => {
      const residualRow = matrix.residuals[t] || [];
      const sourceRow = matrix.sources[t] || [];
      const ts = perTs[t]?.mse;
      return [
        iso,
        ts == null ? '' : ts.toString(),
        ...slotIdx.map((s) => {
          const src = sourceRow[s] || SOURCES.MISSING;
          const r = residualRow[s];
          if (src === SOURCES.MISSING || typeof r !== 'number' || !Number.isFinite(r)) {
            return `;${src}`;
          }
          return `${r};${src}`;
        }),
      ];
    });
    return [header, ...rows].map((cells) => cells.join('\t')).join('\n');
  };

  const handleCopy = async () => {
    const tsv = buildTsv();
    if (!tsv) return;
    if (copyResetRef.current) window.clearTimeout(copyResetRef.current);
    try {
      await navigator.clipboard.writeText(tsv);
      setCopyState('ok');
    } catch (_err) {
      // Fallback for environments without clipboard permission.
      try {
        const ta = document.createElement('textarea');
        ta.value = tsv;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopyState('ok');
      } catch (_err2) {
        setCopyState('err');
      }
    }
    copyResetRef.current = window.setTimeout(() => setCopyState('idle'), 1800);
  };

  // Cell colour encodes the squared contribution (o−f)² — what actually feeds
  // the per-timestamp mean — so depth tracks the score, not the raw residual.
  const maxSq = useMemo(() => {
    if (!matrix) return 0;
    let m = 0;
    for (let t = 0; t < matrix.timestamps.length; t += 1) {
      const row = matrix.residuals[t] || [];
      for (let s = 0; s < matrix.slotCount; s += 1) {
        const r = row[s];
        if (typeof r === 'number' && Number.isFinite(r)) {
          const sq = r * r;
          if (sq > m) m = sq;
        }
      }
    }
    return m;
  }, [matrix]);

  if (!matrix) {
    return (
      <div className="rounded-md border border-gray-200/70 dark:border-gray-600/70 bg-surface dark:bg-gray-870/40 p-6 text-sm text-gray-600 dark:text-gray-400">
        No slot matrix is available for this day. The pipeline persists matrices for the
        deterministic track only, probabilistic (q10/q90) drill-down is deferred.
      </div>
    );
  }

  const cellColor = (residual, source) => {
    // Not-applicable slots carry no residual and aren't scored — render a
    // diagonal hatch so they're distinct from BOTH the dashed-transparent
    // "missing" cells and any solid (low-error cross-horizon) fill.
    if (source === SOURCES.NOT_APPLICABLE) return NOT_APPLICABLE_HATCH;
    if (source === SOURCES.MISSING || source == null) return 'transparent';
    const sq = typeof residual === 'number' && Number.isFinite(residual) ? residual * residual : 0;
    // Perceptual (sqrt) scale so a single large-error row doesn't wash the rest
    // out to a flat colour. sqrt(sq/maxSq) === |r|/maxAbs.
    const ratio = maxSq > 0 ? Math.sqrt(Math.min(1, sq / maxSq)) : 0;
    const alpha = 0.18 + ratio * 0.82;
    const color = SOURCE_COLOR[source] || SOURCE_COLOR[SOURCES.LIVE];
    return withAlpha(color, Number(alpha.toFixed(3)));
  };

  // Slot columns are ordered as the pipeline stores them (longest-lead first).
  const slotIndices = Array.from({ length: matrix.slotCount }, (_, i) => i);
  // Row times are Brussels wall clock; on DST-switch days the zone goes per
  // row too, since 02:xx repeats on the October day.
  const tzLabels = getResourceTimezoneLabels(matrix.timestamps);
  const labelRows = tzLabels.length > 1;

  // Two independent highlights: the pinned row (persistent) and the hovered row
  // (transient preview). They can be the same row or different rows.
  const hoveredRow = hover ? (hover.row != null ? hover.row : hover.t) : null;
  const pinnedRow = selectedIndex;

  const enterRow = (t) => {
    onPreview(t);
  };
  const leaveMatrix = () => {
    setHover(null);
    onPreview(null);
  };

  return (
    <div className="space-y-3">
      {/* Legend (left) + copy action (right) above the matrix. */}
      <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-600 dark:text-gray-400">
        <LegendSwatch color={SOURCE_COLOR[SOURCES.LIVE]} label="Live submission" />
        <LegendSwatch color={SOURCE_COLOR[SOURCES.FWD_FILL]} label="Intraday forward-fill" />
        <LegendSwatch color={SOURCE_COLOR[SOURCES.CROSS_HORIZON]} label="Cross-horizon substitute" />
        <span className="flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded-[2px] border border-dashed border-gray-400 bg-transparent" />
          Missing
        </span>
        <span className="flex items-center gap-1">
          <span
            className="inline-block h-3 w-3 rounded-[2px] border border-gray-300 dark:border-gray-600"
            style={{ background: NOT_APPLICABLE_HATCH }}
          />
          Not applicable
        </span>
        <button
          type="button"
          onClick={handleCopy}
          disabled={!matrix}
          title="Copy as TSV — paste into Excel or Sheets. Each slot cell is `<residual>;<source>` (source ∈ live, fwd_fill, cross_horizon, missing, not_applicable)."
          className={`ml-auto inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] transition-colors ${
            copyState === 'ok'
              ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-700/60 dark:bg-emerald-900/30 dark:text-emerald-200'
              : copyState === 'err'
                ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-700/60 dark:bg-red-900/30 dark:text-red-200'
                : 'border-gray-300 bg-white hover:bg-gray-50 text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-200 disabled:opacity-50'
          }`}
        >
          {copyState === 'ok' ? (
            <>
              <CheckIcon className="h-3 w-3" />
              Copied
            </>
          ) : copyState === 'err' ? (
            <>
              <ClipboardDocumentIcon className="h-3 w-3" />
              Copy failed
            </>
          ) : (
            <>
              <ClipboardDocumentIcon className="h-3 w-3" />
              Copy as TSV
            </>
          )}
        </button>
      </div>

      {/* Height adapts to the viewport so the build-up card below stays in view:
          ~28% of the viewport, capped at 300px (~12 rows) on tall screens; short
          laptops get fewer rows. The rest scrolls. */}
      <div
        className="rounded-md border border-gray-200/70 dark:border-gray-600/70 bg-surface dark:bg-gray-870 overflow-y-auto overflow-x-auto"
        style={{ maxHeight: 'min(300px, 28vh)' }}
        onMouseLeave={leaveMatrix}
      >
        <div
          className="grid"
          style={{
            gridTemplateColumns: `auto auto repeat(${matrix.slotCount}, minmax(18px, 1fr))`,
            gap: '1px',
            backgroundColor: 'rgba(0,0,0,0.06)',
            padding: '1px',
          }}
        >
          {/* Single header row — sticky so it stays visible while scrolling. */}
          <div className="sticky top-0 z-10 bg-surface dark:bg-gray-870 px-2 py-1 text-[10px] font-medium text-gray-700 dark:text-gray-200">
            hh:mm{tzLabels.length > 0 && ` ${tzLabels.join('/')}`}
          </div>
          <div className="sticky top-0 z-10 bg-surface dark:bg-gray-870 px-2 py-1 text-[10px] font-medium text-gray-700 dark:text-gray-200">MSE</div>
          {slotIndices.map((s) => {
            if (isDN) {
              // D+N matrix: each slot column = one submission in the slice.
              // Lead varies per row (different timestamps), so a header
              // lead-time label is misleading — derive the submission's
              // gate-closure clock label from the first row's lead and
              // surface it as the column header.
              const lead0 = (matrix.leadHours[0] || [])[s];
              const ts0 = matrix.timestamps[0];
              let gcLabel = `sub. ${s + 1}`;
              let gcTz = '';
              if (lead0 != null && ts0) {
                const gc = new Date(new Date(ts0).getTime() - lead0 * 3600 * 1000);
                const hm = getHourMinuteInResourceTz(gc);
                if (hm) {
                  gcLabel = hm;
                  // Label at the gate's own instant (CET vs CEST), not now.
                  gcTz = ` ${getResourceTimezoneLabel(undefined, gc)}`;
                }
              }
              return (
                <div
                  key={`hdr-${s}`}
                  className="sticky top-0 z-10 bg-surface dark:bg-gray-870 text-center text-[10px] font-semibold font-mono tabular-nums text-gray-700 dark:text-gray-200 leading-tight py-1"
                  title={`Submission ${s + 1} of ${matrix.slotCount} · gate closure ${gcLabel}${gcTz} · counts equally`}
                >
                  {gcLabel}
                </div>
              );
            }
            const lead = (matrix.leadHours[0] || [])[s];
            return (
              <div
                key={`hdr-${s}`}
                className="sticky top-0 z-10 bg-surface dark:bg-gray-870 text-center text-[10px] font-semibold font-mono tabular-nums text-gray-700 dark:text-gray-200 leading-tight py-1"
                title={`Gate-closure slot ${s + 1} of ${matrix.slotCount} · lead ${lead == null ? '?' : `${lead} h`} · counts equally (arithmetic mean)`}
              >
                {lead == null ? '—' : `${lead}h`}
              </div>
            );
          })}

          {/* Data rows */}
          {matrix.timestamps.map((iso, t) => {
            const isPinned = pinnedRow === t;
            const isHovered = hoveredRow === t;
            const isActive = isPinned || isHovered;
            return (
              <React.Fragment key={`row-${t}`}>
                <div
                  className={`flex items-center px-2 text-[10px] font-mono tabular-nums cursor-pointer ${
                    isPinned
                      ? 'bg-brand-50 dark:bg-brand-900/30 font-semibold text-brand-700 dark:text-brand-300'
                      : isHovered
                        ? 'bg-brand-50/60 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300'
                        : 'bg-surface dark:bg-gray-870 text-gray-600 dark:text-gray-300'
                  }`}
                  onMouseEnter={() => { setHover({ row: t }); enterRow(t); }}
                  onClick={() => onSelect(t)}
                  title={isPinned ? 'Pinned — click again to unpin' : 'Click to pin this row'}
                >
                  {isPinned && (
                    <span className="mr-1 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand-600 dark:bg-brand-400" />
                  )}
                  {formatTime(iso)}
                  {labelRows && iso && ` ${getResourceTimezoneLabel(undefined, new Date(iso))}`}
                </div>
                <div
                  className={`flex items-center px-2 cursor-pointer ${
                    isPinned
                      ? 'bg-brand-50 dark:bg-brand-900/30 ring-2 ring-inset ring-brand-500'
                      : isHovered
                        ? 'bg-brand-50/60 dark:bg-brand-900/20'
                        : 'bg-surface dark:bg-gray-870'
                  }`}
                  title={`Per-timestamp MSE ${formatNumber(perTs[t]?.mse)}`}
                  onMouseEnter={() => { setHover({ row: t }); enterRow(t); }}
                  onClick={() => onSelect(t)}
                >
                  <span className="text-[10px] font-medium font-mono tabular-nums text-gray-800 dark:text-gray-100">
                    {perTs[t]?.mse != null ? compactMSE(perTs[t].mse) : '—'}
                  </span>
                </div>
                {slotIndices.map((s) => {
                  const src = (matrix.sources[t] || [])[s] || SOURCES.MISSING;
                  const isMissing = src === SOURCES.MISSING;
                  return (
                    <div
                      key={`cell-${t}-${s}`}
                      className={`relative cursor-pointer ${isMissing ? 'border border-dashed border-gray-300 dark:border-gray-700' : ''} ${isActive ? 'outline outline-1 outline-brand-400/60' : ''}`}
                      style={{
                        minHeight: 20,
                        background: cellColor((matrix.residuals[t] || [])[s], src),
                      }}
                      onMouseEnter={() => { setHover({ t, s }); enterRow(t); }}
                      onClick={() => onSelect(t)}
                    />
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Hints + caption below the matrix. Colour is set once on this parent
          so every line inherits the exact same grey. */}
      <div className="space-y-1 text-[10px] text-gray-400 dark:text-gray-400">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-0.5 px-1">
          <span className="text-gray-400 dark:text-gray-400">Hover a row to preview its build-up below; click to pin it (click again to unpin).</span>
          <span className="text-gray-400 dark:text-gray-400">Color depth ≈ squared contribution (o−f)².</span>
        </div>
        <div className="px-1">
          {isDN ? (
            <>
              Rows = target timestamps (hh:mm). Columns = the {matrix.slotCount}{' '}
              submission{matrix.slotCount === 1 ? '' : 's'} covering this slice
              (header shows each submission's gate-closure clock). Per-timestamp
              MSE = arithmetic mean of the submissions' squared residuals; every
              submission counts equally, lead time is informational only.
            </>
          ) : (
            <>
              Rows = target timestamps (hh:mm). Columns = 24 gate-closure slots,
              ordered longest lead (left, 23 h) → shortest lead (right, 0 h).
              Per-timestamp MSE = arithmetic mean of the slots' squared residuals;
              every slot counts equally.
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function LegendSwatch({ color, label }) {
  return (
    <span className="flex items-center gap-1">
      <span
        className="inline-block h-3 w-3 rounded-[2px]"
        style={{ background: withAlpha(color, 0.85) }}
      />
      {label}
    </span>
  );
}
